<template lang="pug">
#standard-account-edit(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 一般ユーザー権限変更
    .box-body.register-body
      FormLabel(key-name="氏名（かな）" :value="getName()")
      FormLabel(key-name="電話番号" :value="tel")
      FormLabel(key-name="メールアドレス" :value="mail")
      FormInputStandardUserFunctions(v-model="standardUserFunctionsModel")
      FormInputPermissionResidentGroup(v-if="_useFamilyService" v-model="residentGroups")
      FormInputPermissionOtherUserGroup(v-if="_useFamilyService" v-model="otherUserGroups")
      FormInputShowMessage(v-model="showMessages")

      .register-row(v-show="errorMessage")
        .register-group
          .register-value
            .error-message {{ errorMessage }}

    .box-footer.with-border.register-footer
      ValidationAlert(v-model="showValidationAlert")
      .buttons
        button.btn.update-btn(@click="showConfirmUpdateModal()" type="button") 更新
        button.btn.cancel-btn(@click="gotoStandardAccountList()" type="button") キャンセル

  OkCancelModal(v-model="showUpdateModal" title="一般ユーザー情報を更新しますか？" @ok="doUpdate")
  OkModal(v-model="showUpdateSuccessModal" title="更新に成功しました。" @ok="gotoStandardAccountList")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore, editAccountStore } from "@/store/store";

import CustomValidate from "@/commons/utils/CustomValidate";
import { PcUrl } from "@/commons/constants/PcUrl";
import SystemError from "@/commons/models/SystemError";
import StandardAccountsService, { StandardAccountsPatchModel } from "@/commons/services/StandardAccounts";
import {
  Account,
  Functions,
  ReceiveMessagesFunction,
  ReservationsFunction,
  SendDocumentsFunction,
  SendMessagesFunction,
  ShowMessage,
  WaiterReceiveMessagesFunction
} from "@/commons/services/Authorization";
import AccountsService, { UpdateType } from "@/commons/services/Accounts";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import OtherUserGroupsService from "@/commons/services/OtherUserGroups";
import OtherUserGroupModel from "@/commons/models/OtherUserGroup";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";
import { Permissions } from "@/commons/services/Authorization";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import FormInputShowMessage from "@/pc/components/forms/FormInputShowMessage.vue";
import FormInputPermissionResidentGroup, { PermissionResidentGroupModel } from "@/pc/components/forms/FormInputPermissionResidentGroup.vue";
import FormInputPermissionOtherUserGroup, { PermissionOtherUserGroupModel } from "@/pc/components/forms/FormInputPermissionOtherUserGroup.vue";
import FormInputStandardUserFunctions, { StandardUserFunctionsModel } from "@/pc/containers/settings/FormInputStandardUserFunctions.vue";

/**
 * 一般ユーザー権限変更画面コンポーネント
 */
@Component({
  $_veeValidate: { validator: "new" },
  components: {
    OkCancelModal,
    OkModal,
    PageLoading,
    ValidationAlert,
    FormLabel,
    FormInputShowMessage,
    FormInputPermissionResidentGroup,
    FormInputPermissionOtherUserGroup,
    FormInputStandardUserFunctions
  }
})
export default class StandardAccountEdit extends Vue {
  /** 一般アカウントサービス */
  @Inject("StandardAccountsService") standardAccountsService!: StandardAccountsService;
  /** ユーザーサービス */
  @Inject("AccountsService") accountsService!: AccountsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;
  /** 施設関係者グループサービス */
  @Inject("OtherUserGroupsService") otherUserGroupsService!: OtherUserGroupsService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 更新確認モーダル表示可否 */
  showUpdateModal: boolean = false;
  /** 更新完了モーダル表示可否 */
  showUpdateSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 名前 */
  name: string = "";
  /** 名前かな */
  kana: string = "";
  /** 電話番号 */
  tel: string = "";
  /** メールアドレス */
  mail: string = "";
  /** 使用可能機能Model */
  standardUserFunctionsModel: StandardUserFunctionsModel | null = null;
  /** 使用可能機能 */
  functions: Functions | null = null;
  /** メッセージ送信機能使用可否詳細(家族サービス) */
  sendMessagesFunction: SendMessagesFunction | null = null;
  /** 受信メッセージ機能使用可否詳細(家族サービス) */
  receiveMessagesFunction: ReceiveMessagesFunction | null = null;
  /** 面会予約機能使用可否詳細(家族サービス) */
  reservationsFunction: ReservationsFunction | null = null;
  /** 受信メッセージ機能使用可否詳細(待機者サービス) */
  waiterReceiveMessagesFunction: WaiterReceiveMessagesFunction | null = null;
  /** 文書送付の詳細権限（家族サービス） */
  sendDocumentsFunction: SendDocumentsFunction | null = null;
  /** 権限(利用者グループ) */
  residentGroups: Array<PermissionResidentGroupModel> = [];
  /** 権限(施設関係者グループ) */
  otherUserGroups: Array<PermissionOtherUserGroupModel> = [];
  /** 権限(メッセージ送信の下書きと履歴) */
  showMessages: ShowMessage = ShowMessage.UNKNOWN;
  /** 更新日時 */
  updated: number = 0;

  /** エラーメッセージ */
  errorMessage: string = "";

  get _useFamilyService(): boolean {
    return userStore.facility.useFamilyService;
  }

  /**
   * ### vue lifecycle created
   */
  public created() {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // editAccountStoreが空なら何もしない
    if (!editAccountStore.userId) {
      return;
    }

    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    await this.createView();

    this.isBusy = false;

    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    // ユーザー情報を取得する
    const account: Account = await this.getAccounts();

    this.name = account.name;
    this.kana = account.kana;
    this.tel = account.tel;
    this.mail = account.mail;

    this.functions = account.permissions.functions;
    this.sendMessagesFunction = account.permissions.sendMessagesFunction;
    this.receiveMessagesFunction = account.permissions.receiveMessagesFunction;
    this.reservationsFunction = account.permissions.reservationsFunction;
    this.waiterReceiveMessagesFunction = account.permissions.waiterReceiveMessagesFunction;
    this.sendDocumentsFunction = account.permissions.sendDocumentsFunction;
    this.standardUserFunctionsModel = {
      functions: this.functions,
      reservationsFunction: this.reservationsFunction,
      sendMessagesFunction: this.sendMessagesFunction,
      receiveMessagesFunction: this.receiveMessagesFunction,
      waiterReceiveMessagesFunction: this.waiterReceiveMessagesFunction,
      sendDocumentsFunction: this.sendDocumentsFunction
    };

    this.showMessages = account.permissions.showMessages;
    this.updated = account.updated;

    // 権限の利用者グループの選択肢を作成する
    const residentGroups: Array<PermissionResidentGroupModel> = await this.createResidentGroups(account);
    this.residentGroups = residentGroups;

    // 権限の施設関係者グループの選択肢を作成する
    const otherUserGroups: Array<PermissionOtherUserGroupModel> = await this.createOtherUserGroups(account);
    this.otherUserGroups = otherUserGroups;

    return;
  }

  /**
   * ### アカウント情報を取得する
   */
  public async getAccounts(): Promise<Account> {
    const userId: string = editAccountStore.userId;
    const account: Account = await this.accountsService.post(userStore, userId);
    return account;
  }

  /**
   * ### 権限の利用者グループの選択肢を作成する
   */
  public async createResidentGroups(account: Account): Promise<Array<PermissionResidentGroupModel>> {
    const permissionResidentGroupMap: Map<number, number> = new Map<number, number>();
    for (const permissionGroupId of account.permissions.residentGroups) {
      permissionResidentGroupMap.set(permissionGroupId, permissionGroupId);
    }

    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    let residentGroups: Array<PermissionResidentGroupModel> = [];
    for (const group of groups) {
      const isTarget: boolean = permissionResidentGroupMap.has(group.id) ? true : false;
      residentGroups.push({
        groupId: group.id,
        name: group.name,
        isTarget: isTarget
      });
    }

    return residentGroups;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 権限の利用者グループの選択肢を作成する
   */
  public async createOtherUserGroups(account: Account): Promise<Array<PermissionOtherUserGroupModel>> {
    const permissionOtherUserGroupMap: Map<number, number> = new Map<number, number>();
    for (const permissionGroupId of account.permissions.otherUserGroups) {
      permissionOtherUserGroupMap.set(permissionGroupId, permissionGroupId);
    }

    // 施設関係者グループ種類と種類名の変換用にグルー種類情報のMAPを作る
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.getOtherUserGroupTypes();
    const otherUserGroupTypeNameMap: Map<number, string> = this.createOtherUserGroupTypeNameMap(otherUserGroupTypes);

    const groups: Array<OtherUserGroupModel> = await this.getOtherUserGroups();
    let otherUserGroups: Array<PermissionOtherUserGroupModel> = [];
    for (const group of groups) {
      const permissionGroupId: number | undefined = permissionOtherUserGroupMap.get(group.id);
      const isTarget: boolean = permissionGroupId ? true : false;
      const typeName: string | undefined = otherUserGroupTypeNameMap.get(group.groupTypeId);
      const groupName: string = typeName ? `${typeName}・${group.name}` : group.name;

      otherUserGroups.push({
        groupId: group.id,
        name: groupName,
        isTarget: isTarget
      });
    }

    return otherUserGroups;
  }

  /**
   * ### 施設関係者グループ種類を取得する
   */
  public async getOtherUserGroupTypes(): Promise<Array<OtherUserGroupTypeModel>> {
    const response: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);

    return response;
  }

  /**
   * ### 施設関係者グループ種類IDと施設関係者グループ種類名のMAPを作成する
   */
  public createOtherUserGroupTypeNameMap(otherUserGroupTypes: Array<OtherUserGroupTypeModel>): Map<number, string> {
    let typeMap: Map<number, string> = new Map<number, string>();
    for (const type of otherUserGroupTypes) {
      typeMap.set(type.id, type.name);
    }

    return typeMap;
  }

  /**
   * ### 施設関係者グループを取得する
   */
  public async getOtherUserGroups(): Promise<Array<OtherUserGroupModel>> {
    let response: Array<OtherUserGroupModel> = [];

    try {
      response = await this.otherUserGroupsService.gets(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.otherUserGroupsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300
          };
          response = response.concat(await this.otherUserGroupsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 氏名（かな）を取得する
   */
  public getName(): string {
    return `${this.name}（${this.kana}）`;
  }

  /**
   * ### 登録確認モーダルを表示する
   *
   * - 表示前にバリデーションチェックして、エラーならモーダルは表示しない
   */
  public async showConfirmUpdateModal(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();

    if (validationResult) {
      this.showValidationAlert = false;
      this.showUpdateModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  /**
   * ### 一般ユーザーを更新する
   */
  public async doUpdate(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "更新中...";

    try {
      const requestBody: StandardAccountsPatchModel = this.createRequestBody();
      await this.standardAccountsService.patch(userStore, requestBody);

      editAccountStore.setUserId({ userId: "" });

      this.showUpdateSuccessModal = true;
    } catch (error) {
      // ステータスコードが409の場合、他端末にて更新が行われているので、画面情報を再取得してから再入力を促す
      const systemError: SystemError = error as SystemError;
      if (systemError.statusCode == 409) {
        await this.createView();
        this.errorMessage = "他の端末で更新されたか、表示している情報が古いために更新できませんでした。情報を再読み込みして、もう一度お試しください。";
      }
      // その他のステータスコードの場合は上位に例外を投げなおす
      else {
        throw error;
      }
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 一般ユーザーを更新する
   */
  public createRequestBody(): StandardAccountsPatchModel {
    // 対象とされた権限(利用者グループ)を集計する
    let permissionsResidentGroup: number[] = [];
    for (const group of this.residentGroups) {
      if (group.isTarget == true) {
        permissionsResidentGroup.push(group.groupId);
      }
    }

    // 対象とされた権限(施設関係者グループ)を集計する
    let permissionsOtherUserGroup: number[] = [];
    for (const group of this.otherUserGroups) {
      if (group.isTarget == true) {
        permissionsOtherUserGroup.push(group.groupId);
      }
    }

    // 更新する権限を作成する
    const permissions: Permissions = {
      residentGroups: permissionsResidentGroup,
      otherUserGroups: permissionsOtherUserGroup,
      functions: this.functions as Functions,
      sendMessagesFunction: this.sendMessagesFunction as SendMessagesFunction,
      receiveMessagesFunction: this.receiveMessagesFunction as ReceiveMessagesFunction,
      reservationsFunction: this.reservationsFunction as ReservationsFunction,
      waiterReceiveMessagesFunction: this.waiterReceiveMessagesFunction as WaiterReceiveMessagesFunction,
      sendDocumentsFunction: this.sendDocumentsFunction as SendDocumentsFunction,
      showMessages: this.showMessages
    };

    const requestBody: StandardAccountsPatchModel = {
      userId: editAccountStore.userId,
      updateType: UpdateType.OTHER,
      permissions: permissions,
      updated: this.updated
    };

    return requestBody;
  }

  /**
   * ### 一般ユーザー一覧に遷移する
   */
  public gotoStandardAccountList(): void {
    editAccountStore.setUserId({ userId: "" });
    this.$router.push(PcUrl.StandardAccountList);
    return;
  }
}
</script>

<style lang="scss" scoped>
// 親要素からネストしてスタイルを指定する
#standard-account-edit {
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  .update-btn,
  .cancel-btn {
    width: 120px;
  }
}
</style>
